import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { FormModal, TextInputField, notify } from "@/components/kit";
import { MultiPicker } from "@/components/seo/multi-picker";
import { useAddKeyword } from "@/hooks/api/keyword/useAddKeyword";
import { useGetAllProducts } from "@/hooks/api/product/useGetAllProducts";
import { useUpdateKeyword } from "@/hooks/api/keyword/useUpdateKeyword";
import { useDebounce } from "@/hooks/useDebounce";
import { AddKeyword, Keyword, UpdateKeyword } from "@/types/keyword";

interface AddEditKeywordModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  keyword?: Keyword;
}

const KEYWORD_SCHEMA = z.object({
  id: z.string().optional(),
  keyword: z.string().trim().min(1, "Keyword is required"),
  products: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        nameAr: z.string(),
      }),
    )
    .optional(),
});

type KeywordFormData = z.infer<typeof KEYWORD_SCHEMA>;

const EMPTY: KeywordFormData = { id: "", keyword: "", products: [] };

export const AddEditKeywordModal = ({
  isOpen,
  onClose,
  isEdit,
  keyword,
}: AddEditKeywordModalProps) => {
  const form = useForm<KeywordFormData>({
    resolver: zodResolver(KEYWORD_SCHEMA),
    defaultValues: keyword
      ? {
          id: keyword.id,
          keyword: keyword.keyword,
          products: keyword.products || [],
        }
      : EMPTY,
  });
  const { handleSubmit, control, reset } = form;
  const [productSearch, setProductSearch] = useState("");
  const debouncedSearch = useDebounce(productSearch.trim(), 300);

  // What the picker offers: the first ten products, or what matches what is typed.
  const { data: products, isFetching: isProductsLoading } = useGetAllProducts({
    searchTerm: debouncedSearch,
    page: 1,
    limit: debouncedSearch ? 100 : 10,
  });

  useEffect(() => {
    if (isOpen && keyword) {
      reset({
        id: keyword.id,
        keyword: keyword.keyword,
        products: keyword.products || [],
      });
    } else if (isOpen && !isEdit) {
      reset(EMPTY);
    }
  }, [isOpen, keyword, isEdit, reset]);

  const { mutate: addKeyword, isPending: isAddingKeyword } = useAddKeyword();
  const { mutate: updateKeyword, isPending: isUpdatingKeyword } =
    useUpdateKeyword();

  const handleClose = () => {
    reset(EMPTY);
    setProductSearch("");
    onClose();
  };

  const onSubmitForm = (values: KeywordFormData) => {
    const productIds = values.products?.map((product) => product.id) || [];

    if (isEdit) {
      const payload: UpdateKeyword = {
        id: values.id!,
        keyword: values.keyword,
        productIds,
      };

      updateKeyword(payload, {
        onSuccess: () => {
          notify.success("Keyword updated successfully");
          handleClose();
        },
      });
    } else {
      const payload: AddKeyword = { keyword: values.keyword, productIds };

      addKeyword(payload, {
        onSuccess: () => {
          notify.success("Keyword added successfully");
          handleClose();
        },
        onError: (error: any) => {
          console.error(error);
          notify.error(
            error.message,
            error.response?.data?.message || "Failed to add keyword",
          );
        },
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isAddingKeyword || isUpdatingKeyword}
      submitLabel={isEdit ? "Update" : "Save"}
      title={isEdit ? "Edit Keyword" : "Add Keyword"}
      onClose={handleClose}
      onSubmit={() => handleSubmit(onSubmitForm)()}
    >
      <FormProvider {...form}>
        <TextInputField<KeywordFormData>
          isRequired
          label="Keyword"
          name="keyword"
          placeholder="Enter keyword"
        />
        <Controller
          control={control}
          name="products"
          render={({ field }) => (
            <MultiPicker
              isLoading={isProductsLoading}
              label="Products"
              options={(products?.data ?? []).map((product) => ({
                id: product.id,
                name: product.name,
              }))}
              placeholder="Search and select products"
              selected={(field.value ?? []).map((product) => ({
                id: product.id,
                name: product.name,
              }))}
              onChange={(next) =>
                field.onChange(
                  next.map((option) => {
                    const known = (field.value ?? []).find(
                      (product) => product.id === option.id,
                    );
                    const found = products?.data.find(
                      (product) => product.id === option.id,
                    );

                    return {
                      id: option.id,
                      name: option.name,
                      nameAr: known?.nameAr ?? found?.nameAr ?? "",
                    };
                  }),
                )
              }
              onSearch={setProductSearch}
            />
          )}
        />
      </FormProvider>
    </FormModal>
  );
};
